import React, { useState, useEffect } from "react";
import { Layout, Menu, Row } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  SettingOutlined,
  ShakeOutlined,
  HomeOutlined,
  MoneyCollectOutlined,
  FileZipOutlined,
  AreaChartOutlined,
  AccountBookOutlined,
  ApiOutlined,
  PhoneOutlined,
  SafetyOutlined,
  AppstoreOutlined,
  LineChartOutlined,
  LogoutOutlined,
  LoginOutlined,
  FileTextOutlined,
  UserOutlined,
  MobileOutlined,
} from "@ant-design/icons";
import LayoutHeader from "./header";
import LayoutFooter from "./footer";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { authSelector } from "../../redux/slices/authSlice";
import { useMatchedRoute } from "../../routers";
import IdleSessionTimeout from "./IdleSessionTimeout";
import BreadcrumbItem from "../../components/breadcrumb";
import PermUtils from "../../utils/perm-utils";
import { COMPANY, KIT_SIM, DASHBOARD, DETAIL_PACKAGE, HISTORY, ORDER, OVERVIEW, PACKAGE, ROLE, PACKAGE_API, SIM_PASSPORT, TOP_UP, USER, CUSTOMER, DETAIL, CONNECT, REGISTER_ADDON, USER_PARTNER, DAU_NOI, GIA_HAN, AGENCY, TELESALE, CONFIG, REQUEST, ADMIN, INVOICE, KIT_ORDER, EXTENSION, EXPORT, IMPORT, CREATE, ZALO_ACCOUNT, ACCOUNTS, PERMISSIONS, ZALO_CONFIG, SALES_ORDER } from "../../assets/constants/route_path";
import { getDefaultAuthenticatedRoute } from "../../utils/app-utils";
import logoImg from '../../assets/images/logo-otek.png';
import { APP_VERSION } from "../../config/constants";

const { Sider, Content } = Layout;

type MenuItem = {
  key: any;
  label: string | React.ReactNode;
  link?: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  allowedfunctionids?: string[];
};

const buildMenuItem = ({
  key,
  label,
  link,
  icon,
  children,
  allowedfunctionids,
}: {
  label: string | React.ReactNode;
  key: string;
  link: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  allowedfunctionids?: string[];
}): MenuItem => {
  const item = {
    key,
    label,
    link,
    icon,
    children,
    allowedfunctionids,
  } as MenuItem;
  const hasChildren = children && children.length > 0;

  // Leaf item
  if (link && !hasChildren) {
    item.label = <Link to={link}>{label}</Link>;
  }

  return item;
};

const allItems: MenuItem[] = [
  buildMenuItem({
    key: "Dashboard",
    label: "Dashboard",
    link: DASHBOARD,
    icon: <AreaChartOutlined />,
    allowedfunctionids: [PermUtils.canGetRevenueConnection],
    children: [
      {
        key: DASHBOARD + DAU_NOI,
        label: <Link to={DASHBOARD + DAU_NOI}>Đấu nối</Link>,
        link: DASHBOARD + DAU_NOI,
        allowedfunctionids: [PermUtils.canGetRevenueConnection],
      },
      {
        key: DASHBOARD + GIA_HAN,
        label: <Link to={DASHBOARD + GIA_HAN}>Gia hạn</Link>,
        link: DASHBOARD + GIA_HAN,
        allowedfunctionids: [PermUtils.canGetRevenueConnection],
      },
    ],
  }),
  buildMenuItem({
    key: "Telesale",
    label: "Telesale",
    link: TELESALE,
    icon: <LineChartOutlined />,
    allowedfunctionids: [PermUtils.canGetStatisticalTelesaleCalling],
    children: [
      {
        key: TELESALE + OVERVIEW,
        label: <Link to={TELESALE + OVERVIEW}>Tổng quan</Link>,
        link: TELESALE + OVERVIEW,
      },
      {
        key: TELESALE + COMPANY,
        label: <Link to={TELESALE + COMPANY}>Đối tác</Link>,
        link: TELESALE + COMPANY,
      },
      {
        key: TELESALE + USER,
        label: <Link to={TELESALE + USER}>Nhân sự</Link>,
        link: TELESALE + USER,
      },
    ],
  }),
  buildMenuItem({
    key: "KitSim",
    label: "Đấu nối",
    link: KIT_SIM,
    icon: <ApiOutlined />,
    allowedfunctionids: [PermUtils.canGetListTransactionConnection],
    children: [
      {
        key: KIT_SIM + OVERVIEW,
        label: <Link to={KIT_SIM + OVERVIEW}>Tổng quan</Link>,
        link: KIT_SIM + OVERVIEW,
        allowedfunctionids: [PermUtils.isAdmin],
      },
      {
        key: KIT_SIM + CONNECT,
        label: <Link to={KIT_SIM + CONNECT}>Đấu nối</Link>,
        link: KIT_SIM + CONNECT,
        allowedfunctionids: [PermUtils.canAddTransactionConnectionBySyntax],
      },
      {
        key: KIT_SIM + REQUEST,
        label: <Link to={KIT_SIM + REQUEST}>Yêu cầu đấu nối</Link>,
        link: KIT_SIM + REQUEST,
        allowedfunctionids: [PermUtils.canGetListRegistrationRequests],
      },
      {
        key: KIT_SIM + ORDER,
        label: <Link to={KIT_SIM + ORDER}>Lịch sử đấu nối</Link>,
        link: KIT_SIM + ORDER,
        allowedfunctionids: [PermUtils.canGetListTransactionConnection],
      },
    ],
  }),
  buildMenuItem({
    key: "KitOrder",
    label: "Đấu lô",
    link: KIT_ORDER,
    icon: <AppstoreOutlined />,
    allowedfunctionids: [PermUtils.canGetListOrderTransactionConnection],
    children: [
      {
        key: KIT_ORDER + CONNECT,
        label: <Link to={KIT_ORDER + CONNECT}>Đấu lô</Link>,
        link: KIT_ORDER + CONNECT,
        allowedfunctionids: [PermUtils.canUploadOrderTransactionConnectionByExcel],
      },
      {
        key: KIT_ORDER + ORDER,
        label: <Link to={KIT_ORDER + ORDER}>Lịch sử đấu lô</Link>,
        link: KIT_ORDER + ORDER,
        allowedfunctionids: [PermUtils.canGetListOrderTransactionConnection],
      },
    ],
  }),
  buildMenuItem({
    key: "Invoice",
    label: "Hóa đơn",
    link: INVOICE,
    icon: <FileTextOutlined />,
    allowedfunctionids: [PermUtils.canGetListInvoices],
    children: [
      {
        key: INVOICE + CREATE,
        label: <Link to={INVOICE + CREATE}>Xuất hóa đơn</Link>,
        link: INVOICE + CREATE,
        allowedfunctionids: [PermUtils.canAddInvoiceByForm],
      },
      {
        key: INVOICE + HISTORY,
        label: <Link to={INVOICE + HISTORY}>Hóa đơn</Link>,
        link: INVOICE + HISTORY,
        allowedfunctionids: [PermUtils.canGetListInvoices],
      },
    ],
  }),
  buildMenuItem({
    key: "KitExpire",
    label: "Gia hạn",
    link: EXTENSION,
    icon: <PhoneOutlined />,
    allowedfunctionids: [PermUtils.canGetListTransactionConnectionView],
    children: [
      {
        key: EXTENSION + CUSTOMER,
        label: <Link to={EXTENSION + CUSTOMER}>Khách hàng</Link>,
        link: EXTENSION + CUSTOMER,
        allowedfunctionids: [PermUtils.canGetListCustomerResource],
      },
      {
        key: EXTENSION + AGENCY,
        label: <Link to={EXTENSION + AGENCY}>Đại lý</Link>,
        link: EXTENSION + AGENCY,
        allowedfunctionids: [PermUtils.canGetListPhoneAgencies],
      },
      {
        key: EXTENSION + TELESALE,
        label: <Link to={EXTENSION + TELESALE}>Gia hạn</Link>,
        link: EXTENSION + TELESALE,
        allowedfunctionids: [PermUtils.canGetListTransactionConnectionView],
      },
    ],
  }),
  buildMenuItem({
    key: "SimImport",
    label: "Kho nhập",
    link: IMPORT,
    icon: <LoginOutlined />,
    allowedfunctionids: [PermUtils.canGetListOrderWarehouse, PermUtils.canGetListSimInventory],
    children: [
      {
        key: IMPORT + ORDER,
        label: <Link to={IMPORT + ORDER}>Đơn hàng</Link>,
        link: IMPORT + ORDER,
        allowedfunctionids: [PermUtils.canGetListOrderWarehouse],
      },
      {
        key: IMPORT + HISTORY,
        label: <Link to={IMPORT + HISTORY}>Kiểm tra Serial</Link>,
        link: IMPORT + HISTORY,
        allowedfunctionids: [PermUtils.canGetListSimInventory],
      },
    ],
  }),
  buildMenuItem({
    key: "SimExport",
    label: "Kho xuất",
    link: EXPORT,
    icon: <LogoutOutlined />,
    allowedfunctionids: [PermUtils.canViewExportOrderPage, PermUtils.canGetListSimInventory, PermUtils.canViewSalesExportOrderPage],
    children: [
      {
        key: EXPORT + ORDER,
        label: <Link to={EXPORT + ORDER}>Đơn hàng</Link>,
        link: EXPORT + ORDER,
        allowedfunctionids: [PermUtils.canViewExportOrderPage],
      },
      {
        key: EXPORT + SALES_ORDER,
        label: <Link to={EXPORT + SALES_ORDER}>Đơn hàng bán lẻ</Link>,
        link: EXPORT + SALES_ORDER,
        allowedfunctionids: [PermUtils.canViewSalesExportOrderPage],
      },
      {
        key: EXPORT + HISTORY,
        label: <Link to={EXPORT + HISTORY}>Kiểm tra Serial</Link>,
        link: EXPORT + HISTORY,
        allowedfunctionids: [PermUtils.canGetListSimInventory],
      },
    ],
  }),
  buildMenuItem({
    key: "Company",
    label: "Đối tác",
    link: COMPANY,
    icon: <HomeOutlined />,
    allowedfunctionids: [PermUtils.canGetListCompanies],
    children: [
      {
        key: COMPANY,
        label: <Link to={COMPANY}>Tổng quan</Link>,
        link: COMPANY,
        allowedfunctionids: [PermUtils.canGetListCompanies],
      },
      {
        key: COMPANY + USER_PARTNER,
        label: <Link to={COMPANY + USER_PARTNER}>Quản trị viên</Link>,
        link: COMPANY + USER_PARTNER,
        allowedfunctionids: [PermUtils.canGetListUserPartners],
      },
    ],
  }),
  buildMenuItem({
    key: "Customer",
    label: "Khách hàng",
    link: CUSTOMER,
    icon: <UserOutlined />,
    allowedfunctionids: [PermUtils.canGetListTransactionConnection],
    children: [
      {
        key: CUSTOMER + HISTORY,
        label: <Link to={CUSTOMER + HISTORY}>Lịch sử</Link>,
        link: CUSTOMER + HISTORY,
        allowedfunctionids: [PermUtils.canGetListTransactionConnection],
      },
    ],
  }),
  buildMenuItem({
    key: "Package",
    label: "Gói cước",
    link: PACKAGE,
    icon: <ShakeOutlined />,
    allowedfunctionids: [PermUtils.canSeePackageTab],
    children: [
      {
        key: PACKAGE + OVERVIEW,
        label: <Link to={PACKAGE + OVERVIEW}>Tổng quan</Link>,
        link: PACKAGE + OVERVIEW,
        allowedfunctionids: [PermUtils.canGetListPackages],
      },
      {
        key: PACKAGE + DETAIL,
        label: <Link to={PACKAGE + DETAIL}>Đối tác</Link>,
        link: PACKAGE + DETAIL,
        allowedfunctionids: [PermUtils.canGetListPartnerPackages],
      },
    ],
  }),
  buildMenuItem({
    key: "TopUp",
    label: "Nạp tiền",
    link: TOP_UP,
    icon: <MoneyCollectOutlined />,
    allowedfunctionids: [PermUtils.canCheckBalanceTopup, PermUtils.canGetListOrderTopups, PermUtils.canGetListTransactionTopupsByOrderId],
    children: [
      {
        key: TOP_UP + OVERVIEW,
        label: <Link to={TOP_UP + OVERVIEW}>Tổng quan</Link>,
        link: TOP_UP + OVERVIEW,
        allowedfunctionids: [PermUtils.canCheckBalanceTopup],
      },
      {
        key: TOP_UP + ORDER,
        label: <Link to={TOP_UP + ORDER}>Đơn hàng</Link>,
        link: TOP_UP + ORDER,
        allowedfunctionids: [PermUtils.canGetListOrderTopups],
      },
      {
        key: TOP_UP + HISTORY,
        label: <Link to={TOP_UP + HISTORY}>Lịch sử giao dịch</Link>,
        link: TOP_UP + HISTORY,
        allowedfunctionids: [PermUtils.canGetListTransactionTopupsByOrderId],
      },
    ],
  }),
  buildMenuItem({
    key: "PackageAPI",
    label: "API gói",
    link: PACKAGE_API,
    icon: <AccountBookOutlined />,
    allowedfunctionids: [PermUtils.canGetListOrderTkgDashboard, PermUtils.canGetListTransactionTkgDashboardByOrderId, PermUtils.canGetListPackagesTkgDashboard],
    children: [
      {
        key: PACKAGE_API + ORDER,
        label: <Link to={PACKAGE_API + ORDER}>Đơn hàng</Link>,
        link: PACKAGE_API + ORDER,
        allowedfunctionids: [PermUtils.canGetListOrderTkgDashboard],
      },
      {
        key: PACKAGE_API + HISTORY,
        label: <Link to={PACKAGE_API + HISTORY}>Lịch sử giao dịch</Link>,
        link: PACKAGE_API + HISTORY,
        allowedfunctionids: [PermUtils.canGetListTransactionTkgDashboardByOrderId],
      },
      {
        key: PACKAGE_API + DETAIL_PACKAGE,
        label: <Link to={PACKAGE_API + DETAIL_PACKAGE}>Danh sách gói cước</Link>,
        link: PACKAGE_API + DETAIL_PACKAGE,
        allowedfunctionids: [PermUtils.canGetListPackagesTkgDashboard],
      },
    ],
  }),
  buildMenuItem({
    key: "RegisterAddon",
    label: "Data Sponsor",
    link: REGISTER_ADDON,
    icon: <AccountBookOutlined />,
    allowedfunctionids: [PermUtils.canCheckWalletBalancePostpaid, PermUtils.canGetListOrderDataSponsor, PermUtils.canGetListTransactionDataSponsorByOrderId, PermUtils.canGetListPackagesSponsor],
    children: [
      {
        key: REGISTER_ADDON + OVERVIEW,
        label: <Link to={REGISTER_ADDON + OVERVIEW}>Tổng quan</Link>,
        link: REGISTER_ADDON + OVERVIEW,
        allowedfunctionids: [PermUtils.canCheckWalletBalancePostpaid],
      },
      {
        key: REGISTER_ADDON + ORDER,
        label: <Link to={REGISTER_ADDON + ORDER}>Đơn hàng</Link>,
        link: REGISTER_ADDON + ORDER,
        allowedfunctionids: [PermUtils.canGetListOrderDataSponsor],
      },
      {
        key: REGISTER_ADDON + HISTORY,
        label: <Link to={REGISTER_ADDON + HISTORY}>Lịch sử giao dịch</Link>,
        link: REGISTER_ADDON + HISTORY,
        allowedfunctionids: [PermUtils.canGetListTransactionDataSponsorByOrderId],
      },
      {
        key: REGISTER_ADDON + DETAIL_PACKAGE,
        label: <Link to={REGISTER_ADDON + DETAIL_PACKAGE}>Danh sách gói cước</Link>,
        link: REGISTER_ADDON + DETAIL_PACKAGE,
        allowedfunctionids: [PermUtils.canGetListPackagesSponsor],
      },
    ],
  }),
  buildMenuItem({
    key: "Passport",
    label: "Sim Passport",
    link: SIM_PASSPORT,
    icon: <FileZipOutlined />,
    allowedfunctionids: [PermUtils.canGetListSimPassports],
  }),
  buildMenuItem({
    key: "ZaloAccount",
    label: "Quản lý zalo",
    link: ZALO_ACCOUNT,
    icon: <MobileOutlined />,
    allowedfunctionids: [PermUtils.canGetListZaloAccounts],
    children: [
      {
        key: ZALO_ACCOUNT + ACCOUNTS,
        label: <Link to={ZALO_ACCOUNT + ACCOUNTS}>Tài khoản zalo</Link>,
        link: ZALO_ACCOUNT + ACCOUNTS,
        allowedfunctionids: [PermUtils.canGetListZaloAccounts],
      },
      {
        key: ZALO_ACCOUNT + PERMISSIONS,
        label: <Link to={ZALO_ACCOUNT + PERMISSIONS}>Phân quyền</Link>,
        link: ZALO_ACCOUNT + PERMISSIONS,
        allowedfunctionids: [PermUtils.canGetListZaloAccountUserPermissions],
      },
      {
        key: ZALO_ACCOUNT + ZALO_CONFIG,
        label: <Link to={ZALO_ACCOUNT + ZALO_CONFIG}>Cấu hình Zalo</Link>,
        link: ZALO_ACCOUNT + ZALO_CONFIG,
        allowedfunctionids: [PermUtils.isAdmin],
      },
    ],
  }),
  buildMenuItem({
    key: "Admin",
    label: "Admin",
    link: ADMIN,
    icon: <SafetyOutlined />,
    allowedfunctionids: [PermUtils.isAdmin],
    children: [
      {
        key: ADMIN + USER,
        label: <Link to={ADMIN + USER}>Quản trị viên</Link>,
        link: ADMIN + USER,
      },
      {
        key: ADMIN + ROLE,
        label: <Link to={ADMIN + ROLE}>Phân quyền</Link>,
        link: ADMIN + ROLE,
      },
      {
        key: ADMIN + CONFIG,
        label: <Link to={ADMIN + CONFIG}>Cấu hình</Link>,
        link: ADMIN + CONFIG,
      },
    ],
  }),
];

const isAuthorizedItem = (item: MenuItem, userFnIDs: string[]): boolean => {
  if (item?.allowedfunctionids && item?.allowedfunctionids?.length > 0) {
    return item?.allowedfunctionids?.some((id) => userFnIDs?.includes(id));
  }

  return true;
};

const authorizeMenuItems = (
  items: MenuItem[] = [],
  userFnIDs: string[] = [],
): MenuItem[] => {
  // Filter active items for origin array
  items = items?.filter((item) => isAuthorizedItem(item, userFnIDs));

  // Filter active items for nested items as children
  items?.forEach((item) => {
    if (item?.children && item?.children?.length > 0) {
      item.children = authorizeMenuItems(item.children, userFnIDs);
    }
  });

  return items;
};

const listAllActiveItems = (
  items: MenuItem[],
  currentPath: string | undefined,
): MenuItem[] => {
  const activeItems = items.filter(
    (item) => item.link && currentPath?.startsWith(item.link),
  );

  const childActiveItems = activeItems
    .map((item) => {
      if (item.children && item.children.length > 0) {
        return listAllActiveItems(item.children, currentPath);
      }

      return [];
    })
    .flat();

  return [...activeItems, ...childActiveItems];
};

const getLevelKeys = (items1: MenuItem[]) => {
  const key: Record<string, number> = {};
  const func = (items2: any, level = 1) => {
    items2.forEach((item: any) => {
      if (item.key) {
        key[item.key] = level;
      }
      if (item.children) {
        func(item.children, level + 1);
      }
    });
  };
  func(items1);
  return key;
};

const MainLayout = ({ children }: any) => {
  const authState = useSelector(authSelector);
  const { account } = authState;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  const currentRoutePath = useMatchedRoute();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  console.log("account", account)

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 992;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
        setMobileMenuVisible(false); // Hide mobile menu on resize
      } else {
        setMobileMenuVisible(false); // Reset mobile menu state on desktop
      }
    };

    // Check initial window size
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add beautiful menu styles with red theme and mobile responsiveness
  const menuStyles = `
    .beautiful-menu .ant-menu-item {
      margin: 4px 0 !important;
      border-radius: 12px !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
      background: transparent !important;
      border: 1px solid transparent !important;
      color: rgba(255, 255, 255, 0.85) !important;
      font-weight: 500 !important;
    }
    
    .beautiful-menu .ant-menu-item:hover {
      background: rgba(255, 255, 255, 0.15) !important;
      border: 1px solid rgba(255, 255, 255, 0.25) !important;
      color: white !important;
      transform: translateX(4px) !important;
      box-shadow: 0 4px 12px rgba(250, 93, 103, 0.3) !important;
    }
    
    .beautiful-menu .ant-menu-item-selected {
      background: rgba(255, 255, 255, 0.2) !important;
      border: 1px solid rgba(255, 255, 255, 0.3) !important;
      color: white !important;
      font-weight: 600 !important;
      box-shadow: 0 4px 16px rgba(250, 93, 103, 0.4) !important;
    }
    
    .beautiful-menu .ant-menu-submenu-title {
      margin: 4px 0 !important;
      border-radius: 12px !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
      background: transparent !important;
      border: 1px solid transparent !important;
      color: rgba(255, 255, 255, 0.9) !important;
      font-weight: 600 !important;
    }
    
    .beautiful-menu .ant-menu-submenu-title:hover {
      background: rgba(255, 255, 255, 0.15) !important;
      border: 1px solid rgba(255, 255, 255, 0.25) !important;
      color: white !important;
      transform: translateX(4px) !important;
      box-shadow: 0 2px 8px rgba(250, 93, 103, 0.2) !important;
    }
    
    .beautiful-menu .ant-menu-submenu-open > .ant-menu-submenu-title {
      background: rgba(255, 255, 255, 0.2) !important;
      border: 1px solid rgba(255, 255, 255, 0.3) !important;
      color: white !important;
      box-shadow: 0 2px 8px rgba(250, 93, 103, 0.25) !important;
    }
    
    .beautiful-menu .ant-menu-sub {
      background: rgba(214, 48, 49, 0.15) !important;
      border-radius: 8px !important;
      margin: 4px 0 !important;
      backdrop-filter: blur(10px) !important;
      border: 1px solid rgba(255, 255, 255, 0.1) !important;
    }
    
    .beautiful-menu .ant-menu-item-icon {
      font-size: 16px !important;
      margin-right: 12px !important;
      color: rgba(255, 255, 255, 0.8) !important;
    }
    
    .beautiful-menu .ant-menu-item:hover .ant-menu-item-icon,
    .beautiful-menu .ant-menu-item-selected .ant-menu-item-icon {
      color: white !important;
    }
    
    /* Mobile responsiveness */
    @media (max-width: 992px) {
      .ant-layout-sider {
        z-index: 1001 !important;
      }
    }
  `;

  // Avoid execute these functions multiple times
  const authorizedItems = authorizeMenuItems(allItems, account.listPermission);

  // Always expand all items
  const openedKeys = authorizedItems.map((item) => item.key);

  const activeItems = listAllActiveItems(authorizedItems, currentRoutePath);

  const activeItemLength = activeItems.length;
  const activeItemKeys = activeItems.map((item) => item.key);
  const selectedKey =
    activeItemLength === 0 ? null : activeItemKeys[activeItemLength - 1];

  const levelKeys = getLevelKeys(authorizedItems);
  const [stateOpenKeys, setStateOpenKeys] = useState(['2', '23']);

  const getSiderFooter = () => {
    const isMenuOpen = isMobile ? mobileMenuVisible : !collapsed;

    return <div style={{
      background: 'linear-gradient(135deg, #bc0b16 0%, #3d0808 100%)',
      border: 'none',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      transition: 'all 0.3s ease',
      minHeight: '48px',
      fontSize: 14,
      position: 'relative',
      bottom: 0,
      left: 0,
      right: 0
    }}>{isMenuOpen ? (
      <Row
        justify='space-between'
        align='middle'
        style={{ width: '100%', padding: isMobile ? "0" : "0 1rem" }}>
        <div style={{ fontSize: 12 }}>
          <SettingOutlined /> Phiên bản {APP_VERSION}
        </div>
        <div>
          <LeftOutlined />
        </div>
      </Row>
    ) : (
      <div style={{
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        margin: '0 auto',
        padding: '4px'
      }}>
        <RightOutlined />
      </div>
    )}
    </div>;
  };

  const onOpenChange = (openKeys: any) => {
    const currentOpenKey = openKeys.find((key: any) => stateOpenKeys.indexOf(key) === -1);
    // open
    if (currentOpenKey !== undefined) {
      const repeatIndex = openKeys
        .filter((key: any) => key !== currentOpenKey)
        .findIndex((key: any) => levelKeys[key] === levelKeys[currentOpenKey]);

      setStateOpenKeys(
        openKeys
          // remove repeat key
          .filter((_: any, index: any) => index !== repeatIndex)
          // remove current level all child
          .filter((key: any) => levelKeys[key] <= levelKeys[currentOpenKey]),
      );
    } else {
      // close
      setStateOpenKeys(openKeys);
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: menuStyles }} />

      {/* Mobile overlay */}
      {isMobile && mobileMenuVisible && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            transition: 'all 0.3s ease'
          }}
          onClick={() => setMobileMenuVisible(false)}
        />
      )}

      <Layout hasSider>
        <Sider
          breakpoint='md'
          collapsedWidth={isMobile ? 0 : 80}
          onBreakpoint={(broken) => {
            console.log('Breakpoint triggered:', broken);
            setIsMobile(broken);
            if (broken) {
              setCollapsed(true);
              setMobileMenuVisible(false);
            }
          }}
          style={{
            overflow: "hidden",
            height: "100vh",
            position: "fixed",
            left: 0,
            top: 0,
            bottom: 0,
            background: 'linear-gradient(#b9141f 0%, #140303 100%)',
            boxShadow: '4px 0 20px rgba(250, 93, 103, 0.25)',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            transform: isMobile ? (mobileMenuVisible ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
            visibility: isMobile ? 'visible' : 'visible',
            display: 'flex',
            flexDirection: 'column'
          }}
          width={250}
          collapsible
          collapsed={isMobile ? false : collapsed}
          onCollapse={(value) => {
            if (isMobile) {
              setMobileMenuVisible(!mobileMenuVisible);
            } else {
              setCollapsed(value);
            }
          }}
          trigger={getSiderFooter()}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }}>
            <Row
              justify='center'
              style={{
                background: "rgba(255, 255, 255, 0.15)",
                cursor: "pointer",
                margin: "8px",
                borderRadius: "12px",
                transition: "all 0.3s ease",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                boxShadow: "0 4px 12px rgba(250, 93, 103, 0.2)",
                flexShrink: 0
              }}
              onClick={() => navigate(getDefaultAuthenticatedRoute(account))}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.25)"
                e.currentTarget.style.transform = "translateY(-2px)"
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(250, 93, 103, 0.3)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)"
                e.currentTarget.style.transform = "translateY(0)"
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(250, 93, 103, 0.2)"
              }}
            >
              {collapsed ? (
                <img
                  src={logoImg}
                  alt='Otek'
                  style={{
                    width: 60,
                    objectFit: "cover",
                    objectPosition: "center",
                    borderRadius: "8px",
                    filter: "brightness(1.1) contrast(1.1)",
                    transition: "all 0.3s ease"
                  }}
                />
              ) : (
                <img
                  src={logoImg}
                  alt='Otek'
                  style={{
                    height: 50,
                    maxWidth: "200px",
                    objectFit: "contain",
                    filter: "brightness(1.1) contrast(1.1)",
                    transition: "all 0.3s ease"
                  }}
                />
              )}
            </Row>
            <div style={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Menu
                theme='dark'
                mode='inline'
                defaultOpenKeys={openedKeys}
                items={authorizedItems}
                selectedKeys={selectedKey}
                openKeys={stateOpenKeys}
                onOpenChange={onOpenChange}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '14px',
                  flex: 1
                }}
                className="beautiful-menu"
              />
            </div>
          </div>
        </Sider>
        <Layout
          className='site-layout'
          style={{
            marginLeft: isMobile ? 0 : (collapsed ? 80 : 250),
            transition: "margin-left 0.3s ease",
          }}>
          <LayoutHeader
            profile={account}
            collapsed={collapsed}
            isMobile={isMobile}
            onMobileMenuToggle={() => setMobileMenuVisible(!mobileMenuVisible)}
          />
          <div
            style={{
              padding: 12,
              marginTop: 64,
              paddingBottom: 0,
            }}>
            <BreadcrumbItem />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: "calc(100vh - 100px)",
            }}>
            <Content
              style={{
                padding: 12,
                flexGrow: 1,
              }}>
              <div
                style={{
                  minHeight: "100%",
                  backgroundColor: "white",
                }}>
                {children}
              </div>
            </Content>
            <LayoutFooter />
          </div>
        </Layout>
        <IdleSessionTimeout account={authState.account} />
      </Layout>
    </>
  );
};

export default MainLayout;
